import {
  BarChart as BarChartIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Email as EmailIcon,
  MenuBook as MenuBookIcon,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Restaurant as RestaurantIcon,
  School as SchoolIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Rating,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Dietitian, dietitianApi } from '../services/dietitianApi';
import { ratingApi } from '../services/ratingApi';
import { colors } from '../theme';
import './WelcomePage.css';

const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { userRole, loading } = useAuth();
  const { showError } = useToast();
  const [dietitians, setDietitians] = useState<Dietitian[]>([]);
  const [ratingSummaries, setRatingSummaries] = useState<Map<string, { averageScore: number; totalCount: number }>>(new Map());
  const [dietitiansLoading, setDietitiansLoading] = useState(true);
  const [detailDietitian, setDetailDietitian] = useState<Dietitian | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Diyetisyenleri yükle (online danışan alanlar)
  const loadDietitians = useCallback(async () => {
    try {
      setDietitiansLoading(true);
      const list = await dietitianApi.getOnlineDietitians();
      setDietitians(list);
      const summaries = new Map<string, { averageScore: number; totalCount: number }>();
      await Promise.all(
        list.map(async (d) => {
          const s = await ratingApi.getSummary(d.id).catch(() => ({ averageScore: 0, totalCount: 0 }));
          summaries.set(d.id, s);
        })
      );
      setRatingSummaries(summaries);
    } catch {
      setDietitians([]);
    } finally {
      setDietitiansLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDietitians();
  }, [loadDietitians]);

  // Otomatik kaydırma (5 saniyede bir)
  useEffect(() => {
    if (dietitians.length <= 1) return;
    autoScrollRef.current = setInterval(() => {
      const el = carouselRef.current;
      if (!el) return;
      const cardWidth = 320;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 0) return;
      const next = Math.min(el.scrollLeft + cardWidth, maxScroll);
      el.scrollTo({ left: next, behavior: 'smooth' });
      if (next >= maxScroll) el.scrollTo({ left: 0, behavior: 'smooth' });
    }, 5000);
    return () => {
      if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    };
  }, [dietitians.length]);

  const scrollCarousel = (dir: 'left' | 'right') => {
    const el = carouselRef.current;
    if (!el) return;
    const step = 340;
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
  };

  const getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const handleMessageClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (userRole !== 'client') {
      showError('Mesaj atabilmek için danışan olarak giriş yapmalısınız.', 'Giriş gerekli');
      navigate('/client/login');
      return;
    }
    navigate('/client/select-dietitian');
  };

  // Giriş yapmış danışan veya diyetisyen kendi paneline yönlendirilsin
  useEffect(() => {
    if (loading) return;
    if (userRole === 'dietitian') {
      navigate('/dietitian', { replace: true });
      return;
    }
    if (userRole === 'client') {
      navigate('/client', { replace: true });
      return;
    }
  }, [loading, userRole, navigate]);

  // Yönlendirme yapılacaksa welcome içeriğini gösterme
  if (!loading && (userRole === 'dietitian' || userRole === 'client')) {
    return null;
  }

  return (
    <div className="welcome-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Danışan Yönetiminizi
              <span className="highlight"> Profesyonelleştirin</span>
            </h1>
            <p className="hero-description">
              Modern ve kullanıcı dostu platformumuzla danışanlarınızı daha
              etkili yönetin. Online randevular, diyet programları ve ilerleme
              takibi tek bir yerden.
            </p>
            <div className="hero-buttons">
              <button
                className="btn btn-primary"
                onClick={() => navigate('/dietitian/register')}
              >
                Diyetisyen Olarak Başla
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate('/client/login')}
              >
                Danışan Girişi
              </button>
            </div>
            <p
              style={{
                marginTop: '16px',
                fontSize: '0.9rem',
                opacity: 0.8,
                color: '#fff',
              }}
            >
              Zaten hesabınız var mı?{' '}
              <button
                onClick={() => navigate('/dietitian/login')}
                style={{
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  width: 'auto',
                  height: 'auto',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                }}
              >
                Diyetisyen Girişi
              </button>
            </p>
          </div>
          <div className="hero-image">
            <div className="stats-showcase">
              <div className="stat-card">
                <div className="stat-icon">
                  <CheckCircleIcon sx={{ fontSize: 40, color: '#fff' }} />
                </div>
                <div className="stat-content">
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                    KVKK
                  </h3>
                  <p>Uyumlu Platform</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <PeopleIcon sx={{ fontSize: 40, color: '#fff' }} />
                </div>
                <div className="stat-content">
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                    7/24
                  </h3>
                  <p>Teknik Destek</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <BarChartIcon sx={{ fontSize: 40, color: '#fff' }} />
                </div>
                <div className="stat-content">
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>
                    SSL
                  </h3>
                  <p>Güvenli Veri</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2 className="section-title">İşinizi Kolaylaştıran Özellikler</h2>
          <p className="section-description">
            Danışan yönetiminizi profesyonelleştiren ve zaman kazandıran araçlar
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <PeopleIcon sx={{ fontSize: 48, color: colors.primary.main }} />
            </div>
            <h3 className="feature-title">Danışan Yönetimi</h3>
            <p className="feature-description">
              Tüm danışanlarınızı tek panelden görüntüleyin ve yönetin
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <CalendarIcon sx={{ fontSize: 48, color: colors.primary.main }} />
            </div>
            <h3 className="feature-title">Randevu Planlama</h3>
            <p className="feature-description">
              Online randevularınızı otomatik olarak planlayın ve takip edin
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <RestaurantIcon
                sx={{ fontSize: 48, color: colors.primary.main }}
              />
            </div>
            <h3 className="feature-title">Diyet Programları</h3>
            <p className="feature-description">
              Her danışana özel beslenme programları oluşturun ve paylaşın
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <TrendingUpIcon
                sx={{ fontSize: 48, color: colors.primary.main }}
              />
            </div>
            <h3 className="feature-title">İlerleme Takibi</h3>
            <p className="feature-description">
              Danışan kilo ve ölçümlerini grafik ve raporlarla izleyin
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <MessageIcon sx={{ fontSize: 48, color: colors.primary.main ,width:'fit-content'}} />
            </div>
            <h3 className="feature-title">Anlık Mesajlaşma</h3>
            <p className="feature-description">
              Danışanlarınızla güvenli ve hızlı iletişim kurun
            </p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">
              <NotificationsIcon
                sx={{ fontSize: 48, color: colors.primary.main ,width:'fit-content'}}
              />
            </div>
            <h3 className="feature-title">Akıllı Hatırlatmalar</h3>
            <p className="feature-description">
              KVKK uyumlu, şifreli ve güvenli veri saklama
            </p>
          </div>
        </div>
      </section>

      {/* Diyetisyenlerimiz - Yatay carousel */}
      <section id="dietitians" className="dietitians-section">
        <div className="section-header">
          <h2 className="section-title">Diyetisyenlerimiz</h2>
          <p className="section-description">
            Online danışan alan uzman diyetisyenlerimizle tanışın
          </p>
        </div>
        {dietitiansLoading ? (
          <div className="dietitians-carousel-loading">Yükleniyor...</div>
        ) : dietitians.length === 0 ? (
          <div className="dietitians-carousel-empty">Şu an listelenecek diyetisyen bulunmuyor.</div>
        ) : (
          <div className="dietitians-carousel-wrap">
            <button
              type="button"
              className="carousel-arrow carousel-arrow-left"
              onClick={() => scrollCarousel('left')}
              aria-label="Önceki"
            >
              <ChevronLeftIcon />
            </button>
            <div className="dietitians-carousel" ref={carouselRef}>
              {dietitians.map((d) => {
                const summary = ratingSummaries.get(d.id);
                const avg = summary?.averageScore ?? 0;
                const count = summary?.totalCount ?? 0;
                return (
                  <div
                    key={d.id}
                    className="dietitian-card"
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailDietitian(d)}
                    onKeyDown={(e) => e.key === 'Enter' && setDetailDietitian(d)}
                  >
                    <div className="dietitian-card-inner">
                      <div className="dietitian-card-header">
                        <Avatar
                          src={d.profileImageUrl}
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: colors.primary?.main || '#43a047',
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            border: '2px solid',
                            borderColor: colors.primary?.light || '#66bb6a',
                          }}
                        >
                          {getInitials(d.firstName, d.lastName)}
                        </Avatar>
                        <IconButton
                          className="dietitian-card-message-btn"
                          onClick={(e) => handleMessageClick(e)}
                          size="small"
                          aria-label="Mesaj gönder"
                          sx={{width:'fit-content'}}
                        >
                          <MessageIcon fontSize="small" />
                        </IconButton>
                      </div>
                      <h3 className="dietitian-card-name">
                        {d.firstName} {d.lastName}
                      </h3>
                      {d.specialization && (
                        <Chip
                          label={d.specialization}
                          size="small"
                          sx={{
                            mt: 0.5,
                            mb: 1,
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: colors.alpha?.primary10 || 'rgba(67, 160, 71, 0.1)',
                            color: colors.primary?.dark || '#2e7d32',
                            border: `1px solid ${colors.alpha?.primary20 || 'rgba(67, 160, 71, 0.2)'}`,
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 ,mt:1}}>
                        <Rating
                          value={avg}
                          readOnly
                          size="small"
                          precision={0.1}
                          icon={<StarIcon sx={{ fontSize: 16, color: colors.primary?.main }} />}
                          emptyIcon={<StarIcon sx={{ fontSize: 16, color: '#ddd' }} />}
                        />
                        <Typography component="span" variant="caption" color="text.secondary" fontWeight={600}>
                          {count > 0 ? `${avg.toFixed(1)} · ${count} oy` : 'Henüz oy yok'}
                        </Typography>
                      </Box>
                      {d.bio && (
                        <p className="dietitian-card-bio">
                          {d.bio}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              className="carousel-arrow carousel-arrow-right"
              onClick={() => scrollCarousel('right')}
              aria-label="Sonraki"
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-header">
          <h2 className="section-title">Nasıl Başlarsınız?</h2>
          <p className="section-description">
            Dijital danışan yönetimine geçmek için sadece 3 adım
          </p>
        </div>

        <div className="steps-container">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3 className="step-title">Hesap Oluşturun</h3>
              <p className="step-description">
                Ücretsiz deneme hesabınızı hemen oluşturun
              </p>
            </div>
          </div>

          <div className="step-connector"></div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3 className="step-title">Danışanları Ekleyin</h3>
              <p className="step-description">
                Mevcut danışanlarınızı sisteme kolayca aktarın
              </p>
            </div>
          </div>

          <div className="step-connector"></div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3 className="step-title">Yönetmeye Başlayın</h3>
              <p className="step-description">
                Diyet programı oluşturun, randevu planlayın ve takip edin
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">
            İşinizi Dijitalleştirmeye Hazır mısınız?
          </h2>
          <p className="cta-description">
            Binlerce diyetisyen gibi siz de danışan yönetiminizi
            profesyonelleştirin
          </p>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              className="btn btn-cta"
              onClick={() => navigate('/dietitian/register')}
            >
              Diyetisyen Olarak Başla
            </button>
            <button
              className="btn btn-cta"
              onClick={() => navigate('/client/login')}
              style={{
                background: `linear-gradient(135deg, ${colors.primary.lighter} 0%, ${colors.primary.main} 100%)`,
              }}
            >
              Danışan Girişi
            </button>
          </div>
          <p style={{ marginTop: '16px', fontSize: '0.9rem', opacity: 0.9 }}>
            Ücretsiz başlayın • Kredi kartı gerekmez
          </p>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Diyetisyen detay modal */}
      <Dialog
        open={!!detailDietitian}
        onClose={() => setDetailDietitian(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {detailDietitian && (
          <>
            <DialogTitle
              sx={{
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Avatar
                src={detailDietitian.profileImageUrl}
                sx={{
                  width: 56,
                  height: 56,
                  flexShrink: 0,
                  bgcolor: colors.primary?.main || '#43a047',
                  fontSize: '1.25rem',
                  fontWeight: 700,
                }}
              >
                {getInitials(detailDietitian.firstName, detailDietitian.lastName)}
              </Avatar>
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography variant="h6" fontWeight={700} noWrap>
                  {detailDietitian.firstName} {detailDietitian.lastName}
                </Typography>
                {detailDietitian.specialization && (
                  <Chip
                    label={detailDietitian.specialization}
                    size="small"
                    sx={{
                      mt: 0.5,
                      maxWidth: '100%',
                      bgcolor: colors.alpha?.primary10 || 'rgba(67, 160, 71, 0.1)',
                      color: colors.primary?.dark || '#2e7d32',
                      fontSize: '0.75rem',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />
                )}
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {(() => {
                const r = ratingSummaries.get(detailDietitian.id);
                const avg = r?.averageScore ?? 0;
                const count = r?.totalCount ?? 0;
                return (
                  <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating
                      value={avg}
                      readOnly
                      size="small"
                      precision={0.1}
                      icon={<StarIcon sx={{ fontSize: 20, color: colors.primary?.main }} />}
                      emptyIcon={<StarIcon sx={{ fontSize: 20, color: '#dbdbdb' }} />}
                    />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      {count > 0 ? `${avg.toFixed(1)} · ${count} oy` : 'Henüz oy yok'}
                    </Typography>
                  </Box>
                );
              })()}
              {detailDietitian.bio && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                    Hakkımda
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detailDietitian.bio}
                  </Typography>
                </Box>
              )}
              {detailDietitian.education && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon sx={{ fontSize: 18 }} /> Eğitim
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailDietitian.education}</Typography>
                </Box>
              )}
              {detailDietitian.certifications && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MenuBookIcon sx={{ fontSize: 18 }} /> Sertifikalar
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailDietitian.certifications}</Typography>
                </Box>
              )}
              {(detailDietitian.experienceYears != null && detailDietitian.experienceYears > 0) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WorkIcon sx={{ fontSize: 18 }} /> Deneyim
                  </Typography>
                  <Typography variant="body2">{detailDietitian.experienceYears} yıl</Typography>
                </Box>
              )}
              {(detailDietitian.email || detailDietitian.phone) && (
                <Box sx={{ mt: 2, p: 2, bgcolor: colors.background?.light || '#f5f5f5', borderRadius: 2 }}>
                  {detailDietitian.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: detailDietitian.phone ? 1 : 0 }}>
                      <EmailIcon sx={{ fontSize: 20, color: colors.primary?.main }} />
                      <Typography variant="body2" fontWeight={500}>{detailDietitian.email}</Typography>
                    </Box>
                  )}
                  {detailDietitian.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 20, color: colors.primary?.main }} />
                      <Typography variant="body2" fontWeight={500}>{detailDietitian.phone}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDetailDietitian(null)}
                style={{ marginRight: 'auto',color:colors.text.primary }}
              >
                Kapat
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handleMessageClick();
                  setDetailDietitian(null);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <MessageIcon sx={{width:'fit-content'}} fontSize="small" /> Mesaj Gönder
              </button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default WelcomePage;
